import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SupabaseService } from '../services/supabaseService';
import { supabase } from '../services/supabaseClient';
import { War, BattleSettings } from '../types';
import { checkWarVictory } from '../utils/gameRules';
import { useAuth } from '../context/AuthContext';
import { Shield, Swords, Skull, AlertCircle, Users, Copy, Check, Sword, Wifi, Clock } from 'lucide-react';
import { RoyalConscription } from '../components/RoyalConscription';
import { TacticalFormation } from '../components/TacticalFormation';
import { useAlert } from '../context/AlertContext';

const WarRoom: React.FC = () => {
  const navigate = useNavigate();
  const { success, error } = useAlert();
  const { warId } = useParams();
  const { user, loading: authLoading } = useAuth();

  const [war, setWar] = useState<War | null>(null);
  const [loading, setLoading] = useState(true);
  const [peerOnline, setPeerOnline] = useState(false);

  // AI / Modal States
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [isChoiceModalOpen, setIsChoiceModalOpen] = useState(false);
  const [isFormationModalOpen, setIsFormationModalOpen] = useState(false);
  const [blockedSquares, setBlockedSquares] = useState<number[]>([]);
  const [selectedAIDifficulty, setSelectedAIDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [isDeploying, setIsDeploying] = useState(false);
  const autoDeployTriggered = React.useRef(false);
  const formationLoaded = React.useRef(false);

  // Derived State
  const settings = (war?.settings || {}) as BattleSettings;
  const isHost = war && user ? war.player_id === user.id : false;
  const isAI = settings.opponent_type === 'ai';

  // Initialize formation from saved settings
  useEffect(() => {
    if (!war || !user) return;
    if (formationLoaded.current) return;

    const s = (war.settings || {}) as BattleSettings;
    const saved = isHost ? s.host_formation : s.opponent_formation;

    if (saved) {
      setBlockedSquares(saved);
    }
    formationLoaded.current = true;
  }, [war, user, isHost]);


  // Refactored Readiness Logic
  const hostIsReady = !!settings.host_ready;
  const guestIsReady = war?.opponent_status === 'ready';

  const iAmReady = isAI ? false : (isHost ? hostIsReady : guestIsReady);
  const opponentReady = isHost ? guestIsReady : hostIsReady;
  const bothReady = !isAI && hostIsReady && guestIsReady;

  // Derived: Opponent Presence
  // NOTE: war.opponent_id is the source of truth for a registered opponent.
  const hasOpponent = !!war?.opponent_id;
  const opponentUplinkDetected = hasOpponent || peerOnline;

  // Guest Safety: If Host switches to AI, kick guest
  // This must be BEFORE the early return to function if 'war' updates while mounted
  // Guest Safety: If Host switches to AI, kick guest
  // This must be BEFORE the early return to function if 'war' updates while mounted
  useEffect(() => {
    if (authLoading || !war) return;
    if (!isHost && settings.opponent_type === 'ai') {
      error("Host has switched to AI simulation. Returning to base.");
      navigate('/');
    }
  }, [war?.id, isHost, settings.opponent_type, navigate, error, authLoading]);

  const [kingsLost, setKingsLost] = useState(0);

  const fetchWar = async () => {
    let currentWar;
    if (warId) {
      currentWar = await SupabaseService.getWarById(warId);
    } else {
      currentWar = await SupabaseService.getCurrentWar();
    }

    if (!currentWar) {
      if (warId) console.error("War not found:", warId);
      navigate('/drafting');
      return;
    }

    if (user) {
      const kCount = await SupabaseService.getDeadKingsCount(currentWar.id, user.id);
      setKingsLost(kCount);
    }

    setWar(currentWar);
    setLoading(false);
  };

  useEffect(() => {
    fetchWar();
  }, [navigate, user]); // Added user dependency to ensure we fetch king count correctly

  // Realtime Subscription
  // Realtime Subscription
  useEffect(() => {
    if (!warId || authLoading || !user) return;

    console.log("WarRoom: Initializing Realtime Subscription for", warId);
    const channel = supabase
      .channel(`war_room_${warId}`, {
        config: { presence: { key: user.id } }
      })
      .on(
        'presence',
        { event: 'sync' },
        () => {
          const state = channel.presenceState();
          const pIds = Object.keys(state);
          const others = pIds.filter(id => id !== user.id);
          console.log("WarRoom: Presence Sync", pIds, "Others:", others);
          setPeerOnline(others.length > 0);
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'battles', filter: `war_id=eq.${warId}` },
        (payload: any) => {
          console.log("WarRoom: New Battle Detected", payload.new);
          navigate(`/battle/${payload.new.id}`);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'wars', filter: `id=eq.${warId}` },
        (payload: any) => {
          console.log("WarRoom: War Updated", payload.new);
          setWar(payload.new as War);
        }
      )
      .subscribe(async (status: string, err: any) => {
        if (status === 'SUBSCRIBED') {
          console.log("WarRoom: Realtime Connected Successfully.");
          await channel.track({ user_id: user.id, online_at: new Date().toISOString() });
        } else if (status === 'CHANNEL_ERROR') {
          console.error("WarRoom: Realtime Connection Failed", err);
          error("Realtime connection lost. Please refresh.");
        }
      });

    return () => {
      console.log("WarRoom: Cleaning up subscription");
      supabase.removeChannel(channel);
    };
  }, [warId, navigate, user, authLoading]);

  const handleToggleReady = async () => {
    if (!war || !user) return;

    // If Host+NoOpponent+Unranked -> Prompt Choice
    if (isHost && !opponentUplinkDetected && !iAmReady && !war.is_ranked && !isAI) {
      setIsChoiceModalOpen(true);
      return;
    }

    // Persist formation to DB so it carries over to Battle
    try {
      const update = isHost
        ? { host_formation: blockedSquares }
        : { opponent_formation: blockedSquares };

      await SupabaseService.updateWarSettings(war.id, {
        ...settings,
        ...update
      });
    } catch (e) {
      console.error("Failed to save formation", e);
    }

    if (isAI) {
      setIsDeploying(true);
      await handleDeploy();
    } else {
      await SupabaseService.setWarRoomReady(war.id, isHost, !iAmReady);
    }
  };

  const handleDeploy = async () => {
    if (!war || !user) return;

    // Default: Host plays White, unless overridden by last_winner
    // If I am Guest, this function is only called if I'm creating the battle?
    // Wait -> Only HOST creates the battle in PvP.
    // So 'side' here refers to the Battle Record 'side' property?
    // No, createBattle usually takes 'side' as "Player's Side".
    // But for PvP, the Battle Record needs to know who is White and who is Black.
    // We need to fix createBattle to support PvP assignments.
    // For now, let's assume 'side' = Host's Side.
    let hostSide: 'white' | 'black' = 'white';

    if (war.battles_fought > 0 && war.last_winner) {
      // Loser gets White? Or Winner maintains? "Winner keeps initiative" -> Winner White?
      // Let's alternate. Or Stick.
      // Standard: Alternate specific to matches? Or Random?
      // Let's say: Winner plays White.
      hostSide = war.last_winner === 'player' ? 'white' : 'black';
    }

    const battleSettings = {
      side: hostSide,
      opponent_side: hostSide === 'white' ? 'black' : 'white',
      timeControl: (war.settings?.timeControl || 'unlimited') as any,
      // Pass explicit opponent type
      opponent_type: settings.opponent_type || 'player',
      // Pass explicit opponent ID so the backend knows who the 'enemy' is
      opponent_id: war.opponent_id,
      host_formation: blockedSquares,
      opponent_formation: settings.opponent_formation
    };

    await SupabaseService.createBattle(war.id, battleSettings);
  };



  const confirmAIStart = async () => {
    if (!war) return;
    try {
      await SupabaseService.updateWarSettings(war.id, {
        ...settings,
        opponent_type: 'ai'
      });

      // If switching an existing PvP room to AI, ensure the enemy has pieces to deploy.
      const inv = war.enemy_inventory;
      const count = inv ? Object.values(inv).reduce((a: number, b: number) => a + b, 0) : 0;
      if (count === 0) {
        await SupabaseService.updateEnemyInventory(war.id, { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 });
      }

      setIsAIModalOpen(false);
      setIsDeploying(true);
      await handleDeploy();
    } catch (e) {
      console.error("AI Switch failed", e);
    }
  };

  useEffect(() => {
    if (!bothReady) {
      autoDeployTriggered.current = false;
      return;
    }

    if (!war || !user || !isHost || autoDeployTriggered.current) return;

    autoDeployTriggered.current = true;
    setIsDeploying(true);
    handleDeploy();
  }, [bothReady, isHost, war, user, settings, blockedSquares]);

  if (loading || !war) return <div className="p-8 text-tudor-gold">INITIALIZING...</div>;

  // Victory Check Logic
  const myInventory = isHost ? war.inventory : war.enemy_inventory;
  // Fallback for opponent inventory if not initialized (e.g. AI or early join)
  const oppInventory = isHost ? (war.enemy_inventory || { p: 8 }) : war.inventory;

  const victoryStatus = checkWarVictory(myInventory, oppInventory.p || 0);
  const isDefeat = victoryStatus === 'DEFEAT' || war.status === 'DEFEAT';

  return (
    <div className="p-4 max-w-6xl mx-auto min-h-screen pb-20">

      {/* CHOICE MODAL */}
      {isChoiceModalOpen && !isAI && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-fade-in">
          <div className="w-full max-w-md bg-zinc-900 border border-tudor-gold/30 p-8 rounded-sm relative shadow-2xl text-center">
            <Sword className="w-12 h-12 text-tudor-gold mx-auto mb-4" />
            <h2 className="text-xl font-serif text-white mb-6">Opponent Status Negative</h2>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setIsChoiceModalOpen(false);
                  if (!opponentUplinkDetected) {
                    setIsAIModalOpen(true);
                  }
                }}
                className="w-full p-4 border border-tudor-gold/50 hover:bg-tudor-gold hover:text-black transition-colors uppercase tracking-widest text-sm font-bold"
              >
                Deploy vs AI
              </button>
              <button
                onClick={async () => {
                  setIsChoiceModalOpen(false);
                  // Manually Toggle Ready (Wait mode)
                  if (!war || !user) return;
                  await SupabaseService.setWarRoomReady(war.id, isHost, !iAmReady);
                }}
                className="w-full p-4 border border-zinc-700 hover:border-white text-zinc-400 hover:text-white transition-colors uppercase tracking-widest text-sm"
              >
                Wait for Player
              </button>
            </div>
            <button onClick={() => setIsChoiceModalOpen(false)} className="mt-6 text-xs text-zinc-600 hover:text-zinc-400 underline">Cancel</button>
          </div>
        </div>
      )}

      {/* AI MODAL */}
      {isAIModalOpen && isHost && !opponentUplinkDetected && !isAI && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-fade-in">
          <div className="w-full max-w-md bg-zinc-900 border border-tudor-gold/30 p-8 rounded-sm relative shadow-2xl text-center">
            <h2 className="text-2xl font-serif text-tudor-gold mb-4">Commanding AI</h2>
            <p className="text-zinc-400 mb-6 text-sm">Opponent signal lost. Engage Tactical AI Simulation?</p>

            <div className="flex gap-2 justify-center mb-8">
              {(['easy', 'medium', 'hard'] as const).map(d => (
                <button
                  key={d}
                  onClick={() => setSelectedAIDifficulty(d)}
                  className={`px-4 py-2 border uppercase tracking-wider text-xs transition-colors ${selectedAIDifficulty === d ? 'bg-tudor-gold text-black border-tudor-gold' : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'}`}
                >
                  {d}
                </button>
              ))}
            </div>

            <div className="flex gap-4">
              <button onClick={() => setIsAIModalOpen(false)} className="flex-1 py-3 border border-zinc-700 text-zinc-500 uppercase tracking-widest text-xs hover:text-white">Cancel</button>
              <button onClick={confirmAIStart} className="flex-1 py-3 bg-tudor-gold text-black uppercase tracking-widest text-xs font-bold hover:bg-white">Engage AI</button>
            </div>
          </div>
        </div>
      )}

      <header className="mb-8 border-b border-tudor-gray pb-4 flex justify-between items-end">
        <div>
          <p className="text-gray-400 text-sm mb-1">War ID: <span className="font-mono text-white">{war.id}</span></p>
          <div className="flex items-center space-x-2">
            <span className={`inline-block w-2 h-2 rounded-full ${isDefeat ? 'bg-red-500' : 'bg-green-500'} animate-pulse`}></span>
            <span className="text-sm text-gray-400 font-mono">{isDefeat ? 'CRITICAL FAILURE' : 'OPERATIONAL'}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500 uppercase">Battles Fought</div>
          <div className="text-xl font-mono text-white">{war.battles_fought}</div>
        </div>
      </header>

      {isDefeat && (
        <div className="bg-red-900/20 border border-red-800 p-4 mb-8 text-red-400 flex items-center animate-pulse">
          <AlertCircle className="mr-4" />
          <div>
            <h3 className="font-bold uppercase">War Lost</h3>
            <p className="text-sm">Insufficient forces to maintain defensive line.</p>
            <button
              onClick={() => { SupabaseService.reset(); navigate('/drafting'); }}
              className="mt-2 text-xs border border-red-800 px-2 py-1 hover:bg-red-900"
            >
              RESIGN & RESTART
            </button>
          </div>
        </div>
      )}

      {/* OPPONENT UPLINK - Full Width Top Row (Hidden for AI) */}
      {!isAI && (
        <div className="mb-6 bg-tudor-dark p-4 border border-tudor-gray flex flex-col items-center justify-center relative overflow-hidden group min-h-[90px]">
          {/* Scanning Effect */}
          {!peerOnline ? (
            <div className="flex flex-col md:flex-row items-center gap-6 w-full max-w-5xl justify-center z-10 px-4">


              <div className="flex items-center gap-4 text-gray-400">
                <div className="relative">
                  <Users size={24} className="animate-pulse" />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(234,179,8,0.5)_0%,_transparent_70%)] animate-ping opacity-20"></div>
                </div>
                <div className="flex flex-col">
                  <p className="text-gray-500 text-xs uppercase tracking-widest font-bold leading-none mb-1">Uplink Status</p>
                  <p className="text-tudor-gold font-mono text-sm leading-none animate-pulse">Scanning...</p>
                </div>
              </div>

              <div className="h-12 w-px bg-tudor-gray/20 hidden md:block"></div>

              <div className="flex flex-row gap-0 flex-grow border-tudor-gold w-full items-stretch shadow-lg">
                <div className="bg-black/40 border-y border-l border-tudor-gray/30 px-6 py-4 text-lg text-gray-500 font-mono select-all flex-grow truncate tracking-wide">
                  {`${window.location.origin}/lobby/${war.id}`}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/lobby/${war.id}`);
                    success("Signal coordinates copied.");
                  }}
                  className="bg-tudor-gold/10 hover:bg-tudor-gold/20 border border-tudor-gray/30 hover:border-tudor-gold text-tudor-gold px-6 flex items-center justify-center transition-all group/btn"
                  title="Copy Frequency"
                >
                  <Copy size={24} className="group-hover/btn:scale-110 transition-transform" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-6 z-10">
              <Swords className="text-green-500 animate-pulse" size={28} />
              <div className="flex items-center gap-4">
                <h3 className="text-green-400 font-bold uppercase tracking-widest text-lg">Opponent Detected</h3>
                <p className="text-sm text-gray-500 border-l border-gray-700 pl-4 font-mono">Signal Strong. Ready.</p>
              </div>
            </div>
          )}
          {/* Background Effect */}
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.02)_50%,transparent_75%,transparent_100%)] bg-[length:250%_250%,100%_100%] bg-[position:0_0,0_0] animate-[shimmer_3s_infinite] pointer-events-none"></div>
        </div>
      )}

      {/* TIMEOUT WARNING (Hidden for AI) */}
      {!isAI && (opponentReady || iAmReady) && !bothReady && (
        <div className="mb-6 bg-yellow-900/20 border border-yellow-700/50 p-3 rounded flex items-center justify-center gap-3 animate-pulse">
          <Clock className="w-5 h-5 text-yellow-500" />
          <span className="text-yellow-500 font-mono uppercase tracking-widest text-sm font-bold">
            {opponentReady && !iAmReady ? "⚠ CRITICAL: Deployment Deadline Active (24h)" : "Deployment Clock Active: Opponent has 24h to respond"}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
        {/* Inventory Report */}
        <div className="lg:col-span-2 bg-tudor-dark p-6 border border-tudor-gray">
          <h3 className="text-tudor-gold font-bold uppercase tracking-wider mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2"><Shield size={18} /> Reserve Inventory</div>
            <div className="text-xs text-gray-500">Pawns: {myInventory.p} | Kingsguard: {myInventory.q + myInventory.r + myInventory.b + myInventory.n}</div>
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 text-center">
            {(['p', 'n', 'b', 'r', 'q', 'k'] as const).map((key) => {
              const count = myInventory[key];
              return (
                <div key={key} className={`p-4 rounded border ${count === 0 ? 'bg-red-900/10 border-red-900/30' : 'bg-tudor-black border-gray-800'} flex flex-col items-center justify-center`}>
                  <div className="text-2xl mb-2">{key === 'p' ? '♟' : key === 'n' ? '♞' : key === 'b' ? '♝' : key === 'r' ? '♜' : key === 'q' ? '♛' : '♚'}</div>
                  <div className={`text-xl font-mono ${count === 0 ? 'text-red-500' : 'text-white'}`}>
                    {count}
                  </div>
                  <div className="text-[10px] text-gray-500 uppercase mt-1 opacity-50">{key}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Panel */}
        <div className="bg-tudor-dark p-6 border border-tudor-gray flex flex-col justify-center items-center relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-b from-tudor-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <Swords size={48} className="text-tudor-gray mb-4 group-hover:text-tudor-gold transition-colors duration-300" />

          <div className="text-center z-10 mb-6">
            <p className="text-gray-400 text-sm mb-2">Prepare for next engagement.</p>
            {bothReady ? (
              <p className="text-red-500 font-bold uppercase animate-pulse text-xs">All Commanders Ready - Deploying...</p>
            ) : (
              <div className="flex justify-center gap-4 text-xs font-mono uppercase">
                <span className={iAmReady ? "text-green-500" : "text-yellow-500"}>
                  {iAmReady ? "YOU: READY" : "YOU: PREPARING"}
                </span>
                {!isAI && (
                  <>
                    <span className="text-gray-600">|</span>
                    <span className={opponentReady ? "text-green-500" : "text-red-500"}>
                      {opponentReady ? "OPP: READY" : "OPP: WAITING"}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {!bothReady && !isDeploying ? (
            <button
              onClick={() => {
                if (iAmReady) {
                  // If already ready, stand down
                  handleToggleReady();
                } else {
                  // Open Formation Modal
                  setIsFormationModalOpen(true);
                }
              }}
              disabled={isDefeat || isDeploying}
              className={`w-full font-bold py-4 px-8 rounded transition-all uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2 z-10
                    ${iAmReady
                  ? 'bg-transparent border-2 border-tudor-gold text-tudor-gold hover:bg-tudor-gold/10'
                  : 'bg-tudor-gold text-tudor-black hover:bg-yellow-600 border-2 border-transparent'
                }
                `}
            >
              <span>{iAmReady ? "STAND DOWN" : "DEPLOY TO BATTLE"}</span>
              {iAmReady && <Check size={16} />}
            </button>
          ) : (
            <div className="w-full py-4 text-center text-tudor-gold font-mono uppercase tracking-widest border border-tudor-gold/30 bg-tudor-gold/5 rounded animate-pulse">
              {isDeploying ? "Deploying Forces..." : "Synchronizing Battle..."}
            </div>
          )}
        </div>
      </div>

      {/* FORMATION MODAL (Royal Commission) */}
      {isFormationModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-fade-in overflow-y-auto">
          <div className="w-full max-w-4xl bg-zinc-900 border border-tudor-gold/30 rounded-sm relative shadow-2xl flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-tudor-gold/20 flex justify-between items-center bg-black/40">
              <h2 className="text-xl font-serif text-tudor-gold uppercase tracking-wider flex items-center gap-2">
                <Swords size={20} /> Tactical Commission
              </h2>
              <button onClick={() => setIsFormationModalOpen(false)} className="text-zinc-500 hover:text-white">✕</button>
            </div>

            <div className="p-6 overflow-y-auto custom-scrollbar flex justify-center">
              <TacticalFormation
                inventory={isHost ? war.inventory : war.enemy_inventory}
                side={isHost ? ((war.last_winner === 'enemy') ? 'black' : 'white') : ((war.last_winner === 'enemy') ? 'white' : 'black')}
                blockedSquares={blockedSquares}
                setBlockedSquares={setBlockedSquares}
              />
            </div>

            <div className="p-4 border-t border-tudor-gold/20 bg-black/40 flex justify-end gap-4">
              <button
                onClick={() => setIsFormationModalOpen(false)}
                className="px-6 py-3 border border-zinc-700 text-zinc-400 uppercase tracking-widest text-xs hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setIsFormationModalOpen(false);
                  handleToggleReady();
                }}
                className="px-8 py-3 bg-tudor-gold text-black uppercase tracking-widest text-xs font-bold hover:bg-white shadow-[0_0_15px_rgba(234,179,8,0.3)]"
              >
                Confirm & Deploy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ROYAL CONSCRIPTION SECTION */}
      {!isDefeat && (
        <section className="animate-fade-in-up">
          <RoyalConscription war={war} isHost={isHost} onUpdateInventory={fetchWar} kingsLost={kingsLost} />
        </section>
      )}

    </div>
  );
};

export default WarRoom;
